import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Box,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Event as EventIcon,
  Add as AddIcon,
  Edit as EditIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Gavel as AuctionIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { updateProperty as updatePropertyApi } from '../services/propertyService';
import openScheduleService, { type OpenSchedule, type ScheduleStats } from '../services/openScheduleService';

interface OpenScheduleCardProps {
  propertyId: string;
  agencySlug: string;
  workflows?: Array<{
    _id: string;
    name: string;
    type: string;
    status: string;
    isActive: boolean;
    propertyId?: string; // Added for filtering
  }>;
  property?: {
    consultationStatus?: string;
    saleType?: 'for sale' | 'auction';
    stages?: Array<{
      name: string;
      status: string;
    }>;
  };
}

const OpenScheduleCard: React.FC<OpenScheduleCardProps> = ({ propertyId, agencySlug, workflows = [], property }) => {
  const [schedule, setSchedule] = useState<OpenSchedule | null>(null);
  const [stats, setStats] = useState<ScheduleStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [appointmentLoading, setAppointmentLoading] = useState(false);
  const [appointmentError, setAppointmentError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [propertyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const scheduleData = await openScheduleService.getOpenSchedule(propertyId, agencySlug);
      
      // Ensure only future sessions are in the schedule (additional safety filter on frontend)
      if (scheduleData && scheduleData.sessions && Array.isArray(scheduleData.sessions)) {
        const now = new Date();
        const futureSessions = scheduleData.sessions.filter((session: any) => {
          // Include if status is in_progress (currently happening)
          if (session.status === 'in_progress') {
            return true;
          }
          // Only include scheduled sessions (not completed or cancelled)
          if (session.status !== 'scheduled') {
            return false;
          }
          // Check if session date/time is in the future
          if (!session.date) {
            return false;
          }
          const sessionDate = new Date(session.date);
          if (isNaN(sessionDate.getTime())) {
            return false;
          }
          // If we have a start time, create a proper date-time
          if (session.startTime) {
            try {
              const [hours, minutes] = session.startTime.split(':').map(Number);
              if (!isNaN(hours) && !isNaN(minutes)) {
                sessionDate.setHours(hours, minutes, 0, 0);
              } else {
                sessionDate.setHours(0, 0, 0, 0);
              }
            } catch (e) {
              sessionDate.setHours(0, 0, 0, 0);
            }
          } else {
            sessionDate.setHours(0, 0, 0, 0);
          }
          // Compare dates (accounting for milliseconds)
          return sessionDate.getTime() >= now.getTime();
        });
        
        // Create a new object to ensure React detects the change
        setSchedule({
          ...scheduleData,
          sessions: futureSessions
        });
      } else {
        setSchedule(scheduleData);
      }
      
      // Only get stats if schedule has an ID (not empty)
      if (scheduleData && scheduleData._id) {
        const statsData = await openScheduleService.getScheduleStats(scheduleData._id, agencySlug);
        setStats(statsData);
      } else {
        // Set empty stats for empty schedule
        setStats({
          totalSessions: 0,
          registeredAttendees: 0,
          completedSessions: 0,
          upcomingSessions: 0
        });
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        // No schedule exists yet - this should not happen anymore with the backend change
        setSchedule(null);
      } else {
        setError(err.message || 'Failed to load open schedule data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewSchedule = () => {
    navigate(`/agencies/${agencySlug}/properties/${propertyId}/open-schedule`);
  };

  const handleCreateSchedule = () => {
    navigate(`/agencies/${agencySlug}/properties/${propertyId}/open-schedule`);
  };

  const getPendingInspectionSessions = () => {
    if (!schedule || !schedule.sessions) return [];
    const now = new Date();
    return schedule.sessions.filter((session: any) => {
      if (session.isAuction) return false;
      if (!session.isActive) return false;
      if (session.status !== 'scheduled' && session.status !== 'in_progress') return false;
      if (!session.date) return false;
      const sessionDate = new Date(session.date);
      if (isNaN(sessionDate.getTime())) return false;
      if (session.startTime) {
        try {
          const [hours, minutes] = session.startTime.split(':').map(Number);
          if (!isNaN(hours) && !isNaN(minutes)) {
            sessionDate.setHours(hours, minutes, 0, 0);
          }
        } catch {
          // Fallback to date-only comparison
          sessionDate.setHours(0, 0, 0, 0);
        }
      } else {
        sessionDate.setHours(0, 0, 0, 0);
      }
      return sessionDate.getTime() >= now.getTime();
    });
  };

  const handleInspectionByAppointmentClick = () => {
    setAppointmentError(null);
    const pendingSessions = getPendingInspectionSessions();
    if (pendingSessions.length > 0) {
      setAppointmentDialogOpen(true);
    } else {
      // No pending inspections – apply immediately
      applyInspectionByAppointment();
    }
  };

  const applyInspectionByAppointment = async () => {
    try {
      setAppointmentLoading(true);
      setAppointmentError(null);

      // Mark property as inspection-by-appointment so backend validation and listing feeds honour it.
      // The backend now also cancels any active non-auction sessions as a side effect.
      await updatePropertyApi(agencySlug, propertyId, {
        inspectionByAppointment: true,
      });

      // Refresh schedule to reflect any status changes
      await loadData();

      setAppointmentDialogOpen(false);
    } catch (e: any) {
      console.error('Failed to apply inspection-by-appointment:', e);
      setAppointmentError(
        e?.message || 'Failed to apply Inspection by Appointment. Please try again.'
      );
    } finally {
      setAppointmentLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Check if initial consultation is completed
  const isInitialConsultationCompleted = () => {
    const completedStatuses = ['completed', 'complete', 'done', 'finished'];
    // Only consider consultation workflow for this property
    const consultationWorkflow = workflows.find(w =>
      (w.type && w.type.toLowerCase() === 'consultation' || w.name?.toLowerCase().includes('consultation')) &&
      (w.propertyId === propertyId || w.propertyId?.toString() === propertyId)
    );
    if (
      consultationWorkflow &&
      completedStatuses.includes(consultationWorkflow.status.toLowerCase())
    ) {
      return true;
    }
    // Check legacy property-based consultation
    if (
      property?.consultationStatus &&
      completedStatuses.includes(property.consultationStatus.toLowerCase())
    ) {
      return true;
    }
    // Check legacy stages-based consultation
    if (property?.stages && property.stages.length > 0) {
      const consultationStage = property.stages.find(s =>
        s.name.toLowerCase().includes('consultation')
      );
      if (
        consultationStage &&
        completedStatuses.includes(consultationStage.status.toLowerCase())
      ) {
        return true;
      }
    }
    return false;
  };

  const canCreateSchedule = isInitialConsultationCompleted();

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!schedule || !schedule._id) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <EventIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Open House Schedule</Typography>
          </Box>
          
          {!canCreateSchedule ? (
            <>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Open house scheduling is available after the initial consultation is completed.
              </Typography>
              <Box display="flex" alignItems="center" gap={1} p={1.5} bgcolor="warning.light" borderRadius={1}>
                <WarningIcon sx={{ color: 'warning.dark' }} />
                <Typography variant="body2" color="warning.dark">
                  Complete the initial consultation workflow to enable open house scheduling
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" mb={2}>
                There is no active open house schedule for this property. Create a new schedule to start managing property viewings.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateSchedule}
                size="small"
              >
                Create Schedule
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  const nextSession = openScheduleService.getNextSession(schedule);
  const todaySessions = openScheduleService.getTodaySessions(schedule);
  const pendingInspectionSessions = getPendingInspectionSessions();
  
  // Find auction session from sessions array
  const auctionSession = schedule.sessions?.find((s: any) => s.isAuction === true);
  
  // Get auction info from session or schedule fields
  const auctionDate = auctionSession ? auctionSession.date : schedule.auctionDate;
  const auctionTime = auctionSession ? auctionSession.startTime : schedule.auctionTime;
  const auctionLocation = schedule.auctionLocation;

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center">
            <EventIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Open House Schedule</Typography>
          </Box>
          <Box>
            {/* Use property's saleType if available, otherwise fallback to schedule's saleType */}
            {(() => {
              const displaySaleType = property?.saleType || schedule.saleType || 'sale';
              return (
                <Chip 
                  label={displaySaleType.replace('_', ' ').toUpperCase()} 
                  color={displaySaleType === 'auction' ? 'error' : 'primary'}
                  size="small"
                  sx={{ mr: 1 }}
                />
              );
            })()}
            <Chip 
              label={schedule.status.toUpperCase()} 
              color={schedule.status === 'active' ? 'success' : 'default'}
              size="small"
            />
          </Box>
        </Box>

        <Grid container spacing={2} mb={2}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Total Sessions</Typography>
            <Typography variant="h6">{stats?.totalSessions || 0}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Upcoming</Typography>
            <Typography variant="h6">{stats?.upcomingSessions || 0}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Total Attendees</Typography>
            <Typography variant="h6">{stats?.totalAttendees || 0}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Avg per Session</Typography>
            <Typography variant="h6">{stats?.averageAttendees || 0}</Typography>
          </Grid>
        </Grid>

        {schedule.isFlexible && schedule.extensionCount > 0 && (
          <Box mb={2} p={1.5} bgcolor="warning.light" borderRadius={1}>
            <Typography variant="subtitle2" color="warning.dark">
              ⏰ Extended {schedule.extensionCount} time(s)
            </Typography>
          </Box>
        )}

        {/* Next Schedule and Auction Day */}
        <Grid container spacing={2} mb={2}>
          {nextSession && (
            <Grid item xs={auctionDate ? 6 : 12}>
              <Box p={1.5} bgcolor="info.light" borderRadius={1}>
                <Box display="flex" alignItems="center">
                  <ScheduleIcon sx={{ mr: 1, color: 'info.dark' }} />
                  <Typography variant="subtitle2" color="info.dark">
                    Next Open: {openScheduleService.formatDate(nextSession.date)} at {openScheduleService.formatTime(nextSession.startTime)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
          
          {auctionDate && auctionTime && (() => {
            // Only show auction date if it's in the future
            const now = new Date();
            const auctionDateObj = new Date(auctionDate);
            if (auctionTime) {
              const [hours, minutes] = auctionTime.split(':').map(Number);
              auctionDateObj.setHours(hours || 0, minutes || 0, 0, 0);
            }
            return auctionDateObj >= now;
          })() && (
            <Grid item xs={nextSession ? 6 : 12}>
              <Box 
                p={1.5} 
                sx={{ 
                  bgcolor: 'error.50',
                  border: '1px solid',
                  borderColor: 'error.main',
                  borderRadius: 1 
                }}
              >
                <Box display="flex" alignItems="center">
                  <AuctionIcon sx={{ mr: 1, color: 'error.main' }} />
                  <Typography variant="subtitle2" color="error.main" fontWeight="medium">
                    Auction Day: {openScheduleService.formatDate(auctionDate)} at {openScheduleService.formatTime(auctionTime)}
                  </Typography>
                </Box>
                {auctionLocation && (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                    {auctionLocation}
                  </Typography>
                )}
              </Box>
            </Grid>
          )}
        </Grid>

        {todaySessions.length > 0 && (
          <Box mb={2} p={1.5} bgcolor="success.light" borderRadius={1}>
            <Box display="flex" alignItems="center">
              <PeopleIcon sx={{ mr: 1, color: 'success.dark' }} />
              <Typography variant="subtitle2" color="success.dark">
                Today: {todaySessions.length} session(s)
              </Typography>
            </Box>
          </Box>
        )}

        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleViewSchedule}
            size="small"
          >
            Manage Schedule
          </Button>
          <Button
            variant="outlined"
            startIcon={<ScheduleIcon />}
            onClick={handleInspectionByAppointmentClick}
            size="small"
            disabled={appointmentLoading}
          >
            Inspection by Appointment
          </Button>
          {(property?.saleType === 'auction') && (
            <Button
              variant="outlined"
              startIcon={<AuctionIcon />}
              onClick={() => navigate(`/agencies/${agencySlug}/properties/${propertyId}/auction-scheduling`)}
              size="small"
              color="error"
            >
              Auction Scheduling
            </Button>
          )}
        </Box>

        {/* Optional error message specific to inspection-by-appointment action */}
        {appointmentError && (
          <Box mt={2}>
            <Alert severity="error" onClose={() => setAppointmentError(null)}>
              {appointmentError}
            </Alert>
          </Box>
        )}

        {/* Confirmation dialog when there are pending inspections */}
        <Dialog
          open={appointmentDialogOpen}
          onClose={() => (!appointmentLoading && setAppointmentDialogOpen(false))}
        >
          <DialogTitle>Change to “Inspection by Appointment”?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will cancel the following upcoming open-for-inspection times for this property
              and update the listing so no scheduled inspection times are sent to realestate.com.au.
              Buyers will instead contact you to arrange an inspection.
            </DialogContentText>
            {pendingInspectionSessions.length > 0 && (
              <Box mt={2}>
                {pendingInspectionSessions.map((session: any) => (
                  <Typography variant="body2" key={session._id}>
                    • {openScheduleService.formatDate(session.date)}{' '}
                    {openScheduleService.formatTime(session.startTime)} –{' '}
                    {openScheduleService.formatTime(session.endTime)}
                  </Typography>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setAppointmentDialogOpen(false)}
              disabled={appointmentLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={applyInspectionByAppointment}
              color="primary"
              disabled={appointmentLoading}
            >
              {appointmentLoading ? 'Applying…' : 'Yes, remove inspections'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default OpenScheduleCard; 